import { io } from "socket.io-client";

class WebSocketService {
  constructor() {
    this.socket = null;
    this.atendimentoSocket = null;
    this.isConnected = false;
    this.isAtendimentoConnected = false;
    this._novaMensagemCallbacks = new Set();
    this._statusCallbacks = new Set();
  }

  // Normaliza a URL para o formato correto do Socket.IO
  normalizeWebSocketUrl(url) {
    if (!url) return url;
    // Remove barra final se existir
    return url.replace(/\/$/, '');
  }

  connect(serverUrl = process.env.REACT_APP_WS_URL) {
    if (this.socket?.connected) {
      console.log('⚠️ WebSocket já está conectado');
      return this.socket;
    }

    if (this.socket) {
      this.disconnect();
    }

    const url = this.normalizeWebSocketUrl(serverUrl);

    console.log('🔌 Conectando WebSocket em:', url);

    this.socket = io(url, {
      transports: ["websocket"],
      cors: {
        origin: true,
        credentials: true,
      },
      forceNew: true,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: 3,
      timeout: 20000,
    });

    this.socket.on("connect", () => {
      this.isConnected = true;
      console.log("✅ WebSocket conectado com sucesso");
    });

    this.socket.on("disconnect", (reason) => {
      this.isConnected = false;
      console.log("❌ WebSocket desconectado:", reason);
      
      // Se foi desconexão forçada pelo servidor ou cliente, não reconectar
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        console.log('⚠️ Desconexão intencional - não reconectará automaticamente');
      }
    });

    this.socket.on("connect_error", (error) => {
      console.error("❌ Erro de conexão WebSocket:", error.message);
      this.isConnected = false;
    });

    this.socket.on("reconnect_attempt", (attemptNumber) => {
      console.log(`🔄 Tentativa de reconexão ${attemptNumber}...`);
    });

    this.socket.on("reconnect_failed", () => {
      console.error("❌ Falha ao reconectar WebSocket após todas as tentativas");
      this.isConnected = false;
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // WhatsApp Login
  startLogin(data) {
    if (this.socket) {
      this.socket.emit("login", data);
    }
  }

  logout(data) {
    if (this.socket && this.isConnected) {
      console.log("Enviando evento logoff:", data);
      this.socket.emit("logoff", data);
      return true;
    } else {
      console.error("WebSocket não conectado para logout");
      return false;
    }
  }

  // Event listeners
  onQRCode(callback) {
    if (this.socket) {
      this.socket.on("clientQRCode", callback);
    }
  }

  onAuthenticated(callback) {
    if (this.socket) {
      this.socket.on("authenticated", callback);
    }
  }

  onSessionSaved(callback) {
    if (this.socket) {
      this.socket.on("sessionSaved", callback);
    }
  }

  onAuthFailure(callback) {
    if (this.socket) {
      this.socket.on("auth_failure", callback);
    }
  }

  onLogoff(callback) {
    if (this.socket) {
      this.socket.on("logoffEvent", callback);
    }
  }

  onClearQRCode(callback) {
    if (this.socket) {
      this.socket.on("clearQRCode", callback);
    }
  }

  // Remove listeners
  removeAllListeners() {
    if (this.socket) {
      this.socket.removeAllListeners();
    }
  }

  // Atendimento WebSocket
  connectAtendimento(token) {
    if (this.atendimentoSocket?.connected) {
      console.log('⚠️ WebSocket Atendimento já está conectado');
      return this.atendimentoSocket;
    }

    if (this.atendimentoSocket) {
      this.disconnectAtendimento();
    }

    if (!token) {
      console.error('❌ Token não fornecido para WebSocket Atendimento');
      return null;
    }

    const serverUrl = process.env.REACT_APP_WS_URL || "http://localhost:3001";
    const url = this.normalizeWebSocketUrl(serverUrl);

    console.log('🔌 Conectando WebSocket Atendimento em:', `${url}/atendimento`);
    console.log('🔑 Token:', token.substring(0, 20) + '...');

    this.atendimentoSocket = io(`${url}/atendimento`, {
      auth: { token },
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });

    this.atendimentoSocket.on("connect", () => {
      this.isAtendimentoConnected = true;
      console.log("✅ WebSocket Atendimento conectado com sucesso");
    });

    this.atendimentoSocket.on("disconnect", (reason) => {
      this.isAtendimentoConnected = false;
      console.log("❌ WebSocket Atendimento desconectado:", reason);
      
      // Se foi desconexão forçada pelo servidor ou cliente, não reconectar
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        console.log('⚠️ Desconexão intencional - não reconectará automaticamente');
      }
    });

    this.atendimentoSocket.on("connect_error", (error) => {
      console.error("❌ Erro de conexão WebSocket Atendimento:", error.message);
      console.error("❌ Detalhes:", error);
    });

    this.atendimentoSocket.on("erro", (data) => {
      console.error("❌ Erro do servidor:", data);
      
      // Notificar callbacks de erro pendentes
      if (this._erroCallbacks) {
        this._erroCallbacks.forEach(cb => cb(data));
      }

      // Se for erro de token, redirecionar para login
      if (data.code === 'TOKEN_EXPIRED' || data.code === 'INVALID_TOKEN') {
        console.error("❌ Token inválido ou expirado - necessário fazer login novamente");
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Não redirecionar automaticamente para evitar loop
        // window.location.href = '/login';
      }
    });

    this.atendimentoSocket.on("reconnect_attempt", (attemptNumber) => {
      console.log(`🔄 Tentativa de reconexão Atendimento ${attemptNumber}...`);
    });

    this.atendimentoSocket.on("reconnect_failed", () => {
      console.error("❌ Falha ao reconectar WebSocket Atendimento após todas as tentativas");
    });

    return this.atendimentoSocket;
  }

  disconnectAtendimento() {
    // Um reload agendado não pode sobreviver à conexão que o originou: ele
    // dispararia no socket seguinte com o instanciaId do mount anterior.
    if (this._recarregarTimer) {
      clearTimeout(this._recarregarTimer);
      this._recarregarTimer = null;
    }
    this._recarregarDesde = null;
    if (this.atendimentoSocket) {
      this.atendimentoSocket.disconnect();
      this.atendimentoSocket = null;
      this.isAtendimentoConnected = false;
    }
  }

  // Atendimento methods
  onTodasConversas(callback) {
    if (this.atendimentoSocket) {
      this.atendimentoSocket.off("todas_conversas");
      this.atendimentoSocket.on("todas_conversas", callback);
    }
  }

  onConversasPendentes(callback) {
    if (this.atendimentoSocket) {
      this.atendimentoSocket.on("conversas_pendentes", callback);
    }
  }

  onNovaMensagem(callback) {
    if (this.atendimentoSocket) {
      console.log("🔌 Registrando listener nova_mensagem");
      this.atendimentoSocket.off("nova_mensagem");
      this.atendimentoSocket.on("nova_mensagem", (data) => {
        console.log(
          "📡 Evento nova_mensagem recebido no WebSocketService:",
          data
        );
        callback(data);
        // Despachar para callbacks extras (useMessages)
        this._novaMensagemCallbacks.forEach(cb => cb(data));
      });
    }
  }

  onAtendimentoFinalizado(callback) {
    if (this.atendimentoSocket) {
      console.log("🔌 Registrando listener atendimento_finalizado");
      this.atendimentoSocket.off("atendimento_finalizado");
      this.atendimentoSocket.on("atendimento_finalizado", (data) => {
        console.log("📡 Atendimento finalizado:", data);
        callback(data);
      });
    }
  }

  onStatusAtualizado(callback) {
    if (this.atendimentoSocket) {
      console.log("🔌 Registrando listener status_atualizado");
      this.atendimentoSocket.off("status_atualizado");
      this.atendimentoSocket.on("status_atualizado", (data) => {
        console.log("📊 Status atualizado:", data);
        callback(data);
        this._statusCallbacks.forEach(cb => cb(data));
      });
    }
  }

  onMensagemEnviada(callback) {
    if (this.atendimentoSocket) {
      this.atendimentoSocket.off("mensagem_enviada");
      this.atendimentoSocket.on("mensagem_enviada", callback);
    }
  }

  onTagAplicada(callback) {
    if (this.atendimentoSocket) {
      console.log("🔌 Registrando listener tag_aplicada");
      this.atendimentoSocket.off("tag_aplicada");
      this.atendimentoSocket.on("tag_aplicada", (data) => {
        console.log("🏷️ Evento tag_aplicada recebido:", data);
        callback(data);
      });
    }
  }

  onMensagensCarregadas(callback) {
    if (this.atendimentoSocket) {
      this.atendimentoSocket.on("mensagens_carregadas", callback);
    }
  }

  onErro(callback) {
    if (this.atendimentoSocket) {
      this.atendimentoSocket.on("erro", callback);
    }
  }

  carregarMensagens(data) {
    if (this.atendimentoSocket) {
      this.atendimentoSocket.emit("carregar_mensagens", data);
    }
  }

  /**
   * Recarrega a lista inteira de conversas — é a query mais cara do backend.
   *
   * Debounced porque vários eventos podem pedir recarga em rajada (várias tags,
   * várias mensagens de contato novo). `imediato` para ação do usuário, onde a
   * latência é percebida.
   */
  recarregarConversas(instanciaId = null, { imediato = false } = {}) {
    if (!this.atendimentoSocket) return;

    const emitir = () => {
      this._recarregarTimer = null;
      this._recarregarDesde = null;
      if (this.atendimentoSocket) {
        this.atendimentoSocket.emit("recarregar_conversas", { instanciaId });
      }
    };

    if (this._recarregarTimer) {
      clearTimeout(this._recarregarTimer);
      this._recarregarTimer = null;
    }

    if (imediato) return emitir();

    // Teto de espera. Só com debounce trailing, um cliente movimentado (uma
    // mensagem a cada <800ms) adia a recarga indefinidamente, e thread nova
    // nunca entra na lista.
    const agora = Date.now();
    if (!this._recarregarDesde) this._recarregarDesde = agora;
    if (agora - this._recarregarDesde >= 2500) return emitir();

    this._recarregarTimer = setTimeout(emitir, 800);
  }

  pausarIA(data) {
    if (this.atendimentoSocket) {
      console.log("🛑 Pausando IA para:", data);
      this.atendimentoSocket.emit("pausar_ia", data);
    } else {
      console.error("❌ Socket de atendimento não conectado para pausar IA");
    }
  }

  reativarIA(data) {
    if (this.atendimentoSocket) {
      console.log("▶️ Reativando IA para:", data);
      this.atendimentoSocket.emit("reativar_ia", data);
    } else {
      console.error("❌ Socket de atendimento não conectado para reativar IA");
    }
  }

  finalizarAtendimento(data) {
    if (this.atendimentoSocket) {
      console.log("✅ Finalizando atendimento para:", data);
      this.atendimentoSocket.emit("finalizar_atendimento", data);
    } else {
      console.error("❌ Socket de atendimento não conectado");
    }
  }

  verificarStatusIA(data) {
    if (this.atendimentoSocket) {
      console.log("🔍 Verificando status IA para:", data);
      this.atendimentoSocket.emit("verificar_status_ia", data);
    }
  }

  enviarMensagemDireta(data) {
    if (this.atendimentoSocket) {
      this.atendimentoSocket.emit("enviar_mensagem_direta", data);
    }
  }

  removeAtendimentoListeners() {
    if (this.atendimentoSocket) {
      this.atendimentoSocket.removeAllListeners();
    }
  }
  getSocket() {
    return this.atendimentoSocket;
  }
}

export default new WebSocketService();
